"use client";

import ConnectorSelector from "@/app/(v2)/workflows/_components/ConnectorSelector";
import PushbulletDeviceSelector from "@/app/(v2)/workflows/_components/form-fields/PushbulletDeviceSelector";
import PushoverRecipientSelector from "@/app/(v2)/workflows/_components/form-fields/PushoverRecipientSelector";
import SlackChannelSelector from "@/app/(v2)/workflows/_components/form-fields/SlackChannelSelector";
import TelegramChatSelector from "@/app/(v2)/workflows/_components/form-fields/TelegramChatSelector";
import WebPushSubscriptionSelector from "@/app/(v2)/workflows/_components/form-fields/WebPushSubscriptionSelector";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { extractEmail } from "@/lib/user/extractEmail";
import { cn } from "@/lib/utils";
import { NOTIFICATION_CHANNELS, type NotificationChannelDef, type NotificationConfig, type NotificationDraft } from "@/lib/workflow/notificationChannels";
import { useUser } from "@b3dotfun/sdk/global-account/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
interface NotificationChannelPickerProps {
  value: NotificationConfig | null;
  onChange: (config: NotificationConfig | null) => void;
  // Optional draft state used to rehydrate intermediate picks (e.g. user picked
  // a channel + connector but hasn't entered the destination yet). When present,
  // picker initializes its internal state from draft instead of starting blank.
  initialDraft?: NotificationDraft | null;
  // Fires on every internal change (including partial selections) so callers
  // can persist the draft. `onChange` still fires only when the config is
  // complete; the two are complementary.
  onDraftChange?: (draft: NotificationDraft) => void;
  // Restrict the channel picker to a subset of channels. Used by templates
  // whose downstream nodes only work with one channel type (e.g. a Slack-only
  // approval flow). When length === 1, the single channel is auto-selected on
  // mount. Unknown keys are ignored; an empty/all-unknown list falls back to
  // showing all channels.
  allowedChannels?: string[];
  className?: string;
}
const CHANNEL_KEYS = Object.keys(NOTIFICATION_CHANNELS);
export default function NotificationChannelPicker({
  value,
  onChange,
  initialDraft,
  onDraftChange,
  allowedChannels,
  className
}: NotificationChannelPickerProps) {
  const availableChannelKeys = useMemo(() => {
    if (!allowedChannels || allowedChannels.length === 0) return CHANNEL_KEYS;
    const filtered = allowedChannels.filter(key => key in NOTIFICATION_CHANNELS);
    return filtered.length > 0 ? filtered : CHANNEL_KEYS;
  }, [allowedChannels]);
  // When initialDraft is provided, it is authoritative for every field —
  // including fields the user explicitly cleared back to null/"". Only fall
  // back to `value` when there is no draft at all. When neither is present and
  // the template restricts to a single channel, pre-select it so the user
  // doesn't have to pick from a one-item dropdown.
  const [channelKey, setChannelKey] = useState<string | null>(() => {
    if (initialDraft) return initialDraft.channel;
    if (value?.channel) return value.channel;
    if (availableChannelKeys.length === 1) return availableChannelKeys[0];
    return null;
  });
  const [connectorId, setConnectorId] = useState<string | null>(initialDraft ? initialDraft.connectorId : value?.connectorId ?? null);
  const [destination, setDestination] = useState<string>(initialDraft ? initialDraft.destination : value?.destination ?? "");
  const [destinationLabel, setDestinationLabel] = useState<string>(initialDraft ? initialDraft.destinationLabel : value?.destinationLabel ?? "");
  const [extras, setExtras] = useState<Record<string, string>>(initialDraft ? initialDraft.extras : value?.extras ?? {});
  // Track icon URLs that failed to load so a retried network doesn't stay hidden
  // (React would otherwise keep an imperative style.display:none on reused nodes).
  const [brokenIconUrls, setBrokenIconUrls] = useState<Set<string>>(() => new Set());
  const markIconBroken = useCallback((url: string) => {
    setBrokenIconUrls(prev => {
      if (prev.has(url)) return prev;
      const next = new Set(prev);
      next.add(url);
      return next;
    });
  }, []);

  // Stable refs so effect callbacks don't re-fire on identity changes.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onDraftChangeRef = useRef(onDraftChange);
  onDraftChangeRef.current = onDraftChange;
  const channelDef: NotificationChannelDef | null = channelKey ? NOTIFICATION_CHANNELS[channelKey] ?? null : null;
  const needsConnector = channelDef?.connectorType != null;
  const needsDestination = channelDef?.destinationInput != null;
  const {
    user
  } = useUser();
  const userEmail = extractEmail(user);
  const destinationInputType = channelDef?.destinationInput?.type;
  // Tracks whether we've already prefilled for the current channel selection,
  // so manually clearing the field doesn't immediately refill it.
  const emailPrefilledRef = useRef(false);
  useEffect(() => {
    if (destinationInputType !== "email") {
      emailPrefilledRef.current = false;
      return;
    }
    if (destination || emailPrefilledRef.current || !userEmail) return;
    setDestination(userEmail);
    setDestinationLabel(userEmail);
    emailPrefilledRef.current = true;
  }, [destinationInputType, destination, userEmail]);
  // Stabilize the reference so the emit effect's dep array doesn't churn when
  // channelDef is the same underlying registry entry.
  const extraFields = useMemo(() => channelDef?.extraFields ?? [], [channelDef]);

  // Sync state when the parent provides a non-null external value. Callers
  // that drive the picker via `initialDraft` (e.g. the sidebar, which passes
  // `value={null}`) rely on this effect NOT firing — otherwise it would
  // clobber the just-rehydrated draft on mount with null fields.
  useEffect(() => {
    if (value == null) return;
    const externalChannel = value.channel;
    if (externalChannel !== channelKey) {
      setChannelKey(externalChannel);
      setConnectorId(value.connectorId ?? null);
      setDestination(value.destination ?? "");
      setDestinationLabel(value.destinationLabel ?? "");
      setExtras(value.extras ?? {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.channel]);
  useEffect(() => {
    // Always emit the draft so partial picks can be persisted by the parent
    // (e.g. saved into the virtual node payload).
    onDraftChangeRef.current?.({
      channel: channelKey,
      connectorId,
      destination,
      destinationLabel,
      extras
    });
    if (!channelKey || !channelDef) {
      onChangeRef.current(null);
      return;
    }
    if (needsConnector && !connectorId) {
      onChangeRef.current(null);
      return;
    }
    if (needsDestination && !destination) {
      onChangeRef.current(null);
      return;
    }
    const missingExtra = extraFields.some(field => !extras[field.key]?.trim());
    if (missingExtra) {
      onChangeRef.current(null);
      return;
    }

    // For connector-only channels (no destination step), keep `destination`
    // empty so it never leaks the connector ID through the config.
    const resolvedDestination = needsDestination ? destination : "";
    const resolvedLabel = needsDestination ? destinationLabel || destination : destinationLabel || channelDef.label;
    onChangeRef.current({
      channel: channelKey,
      actionId: channelDef.actionId,
      connectorType: channelDef.connectorType,
      connectorId: needsConnector ? connectorId : null,
      destination: resolvedDestination,
      destinationLabel: resolvedLabel,
      extras: extraFields.length > 0 ? {
        ...extras
      } : undefined
    });
  }, [channelKey, channelDef, needsConnector, needsDestination, connectorId, destination, destinationLabel, extras, extraFields]);
  const handleChannelChange = useCallback((nextChannel: string) => {
    setChannelKey(nextChannel);
    setConnectorId(null);
    setDestination("");
    setDestinationLabel("");
    setExtras({});
  }, []);
  const handleConnectorChange = useCallback((nextConnectorId: string | null) => {
    setConnectorId(nextConnectorId);
    setDestination("");
    setDestinationLabel("");
  }, []);
  const handleExtraChange = useCallback((key: string, nextValue: string) => {
    setExtras(prev => ({
      ...prev,
      [key]: nextValue
    }));
  }, []);
  const renderDestination = () => {
    if (!channelDef || !needsDestination) return null;
    if (needsConnector && !connectorId) return null;
    const inputType = channelDef.destinationInput!.type;
    const inputLabel = channelDef.destinationInput!.label;
    switch (inputType) {
      case "slack-channel":
        return <SlackChannelSelector label={inputLabel} value={destination} onChange={val => {
          const v = Array.isArray(val) ? val[0] ?? "" : val;
          setDestination(v);
        }} connectorId={connectorId ?? undefined} onConnectorChange={id => setConnectorId(id)} onResolvedChange={selections => {
          if (selections.length > 0 && selections[0].channelName) {
            setDestinationLabel(`#${selections[0].channelName}`);
          }
        }} required />;
      case "telegram-chat":
        return <TelegramChatSelector label={inputLabel} value={destination} onChange={val => {
          setDestination(val);
          setDestinationLabel(val);
        }} connectorId={connectorId ?? undefined} onConnectorSelect={id => setConnectorId(id)} required />;
      case "pushover-recipient":
        return <PushoverRecipientSelector label={inputLabel} value={destination} onChange={val => {
          setDestination(val);
          setDestinationLabel(val);
        }} connectorId={connectorId ?? undefined} onConnectorSelect={id => setConnectorId(id)} required />;
      case "pushbullet-device":
        return <PushbulletDeviceSelector label={inputLabel} value={destination} onChange={val => {
          setDestination(val);
          setDestinationLabel(val);
        }} connectorId={connectorId ?? undefined} onConnectorSelect={id => setConnectorId(id)} required />;
      case "webpush-subscription":
        return <WebPushSubscriptionSelector label={inputLabel} value={destination} onChange={val => {
          setDestination(val);
          setDestinationLabel(val);
        }} connectorId={connectorId ?? undefined} onConnectorSelect={id => setConnectorId(id)} required />;
      case "email":
        return <div className="space-y-2">
            <Label className="field-label">{inputLabel}</Label>
            <Input type="email" value={destination} onChange={e => {
            setDestination(e.target.value);
            setDestinationLabel(e.target.value);
          }} placeholder="user@example.com" required />
          </div>;
      case "text":
      default:
        return <div className="space-y-2">
            <Label className="field-label">{inputLabel}</Label>
            <Input type="text" value={destination} onChange={e => {
            setDestination(e.target.value);
            setDestinationLabel(e.target.value);
          }} required />
          </div>;
    }
  };
  const renderExtraFields = () => {
    if (!channelDef || extraFields.length === 0) return null;
    // Require connector (if any) and destination (if any) before exposing
    // extras so the picker steps stay in order.
    if (needsConnector && !connectorId) return null;
    if (needsDestination && !destination) return null;
    return <>
        {extraFields.map(field => <div key={field.key} className="space-y-2">
            <Label className="field-label">{field.label}</Label>
            <Input type={field.type ?? "text"} value={extras[field.key] ?? ""} onChange={e => handleExtraChange(field.key, e.target.value)} placeholder={field.placeholder} required />
          </div>)}
      </>;
  };
  return <div className={cn("space-y-4", className)}>
      {/* Step 1: Channel provider */}
      <div className="space-y-2">
        <Label className="field-label">Notification Channel</Label>
        <Select value={channelKey ?? ""} onValueChange={handleChannelChange}>
          <SelectTrigger>
            <SelectValue placeholder="Select a channel...">
              {channelDef && <span className="flex items-center gap-2">
                  {!brokenIconUrls.has(channelDef.iconUrl) && <img src={channelDef.iconUrl} alt="" className="h-4 w-4 rounded" onError={() => markIconBroken(channelDef.iconUrl)} />}
                  {channelDef.label}
                </span>}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {availableChannelKeys.map(key => {
            const def = NOTIFICATION_CHANNELS[key];
            return <SelectItem key={key} value={key}>
                  <span className="flex items-center gap-2">
                    {!brokenIconUrls.has(def.iconUrl) && <img src={def.iconUrl} alt="" className="h-4 w-4 rounded" onError={() => markIconBroken(def.iconUrl)} />}
                    {def.label}
                  </span>
                </SelectItem>;
          })}
          </SelectContent>
        </Select>
      </div>

      {/* Step 2: Connector (only when channel needs one) */}
      {channelDef && needsConnector && <ConnectorSelector connectorType={channelDef.connectorType!} value={connectorId} onChange={handleConnectorChange} required />}

      {/* Step 3: Destination (skipped for connector-only channels like Discord) */}
      {renderDestination()}

      {/* Step 4: Channel-specific extra fields (e.g. SMS `from` number) */}
      {renderExtraFields()}
    </div>;
}